import { InteractifyKhanoumiPlayer } from '@interactify-live/khanoumi-player';
import { InteractifyKhanoumiPlayerProps, Product } from '@interactify-live/khanoumi-player/dist/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { GetShortVideoListViewModel } from '@/types/cml-api';
import { PUBLIC_DOMAIN_URL } from '@/app/_constants/urls';
import { useNavigateLogin } from '@/app/_utils/session/use-navigate-login';
import useUser from '@/utils/use-user';
import BabyPDP from '../baby-pdp';
import { SPACE_SLUG } from '../constants';
import ProductCard from '../product-card';
import { InteractifyPlayerRef, InteractionType } from '../types';
import { useGetProducts } from '../utils/use-get-products';
import { useGetSingleShort } from '../utils/use-get-single-short';
import { useInteractifyInit } from '../utils/use-short-init';
import { useInteractifyParams } from '../utils/use-vid-params';
interface Props {
  short: GetShortVideoListViewModel;
  isVideoMuted: boolean;
  videoMuteStateChangeHandler: (isMuted: boolean) => void;
  closeHandler: () => void;
  onCommentsPanelOpen?: () => void;
  onCommentsPanelClose?: () => void;
}
const InteractifyPlayer = ({
  short,
  closeHandler,
  onCommentsPanelClose,
  onCommentsPanelOpen,
  isVideoMuted,
  videoMuteStateChangeHandler
}: Props) => {
  const {
    isLoggedIn
  } = useUser();
  const {
    activeInteractifyParam,
    isOpenedFromGallery,
    removeInteractifyParam
  } = useInteractifyParams();
  const [selectedProductSlug, setSelectedProductSlug] = useState<string>();
  const navigateLogin = useNavigateLogin();
  const isActive = activeInteractifyParam === short.slug;
  const {
    data: shortInitData,
    isLoading: isShortLoading
  } = useInteractifyInit(short.slug || '', isActive);
  const {
    data: singleShortData,
    isLoading: isSingleShortLoading,
    isSuccess: isSingleShortSuccess
  } = useGetSingleShort(short.slug || '', isActive);
  const showProducts = !isOpenedFromGallery && isActive && !isShortLoading;
  const products = singleShortData?.products || [];
  const productsData = useGetProducts(products, showProducts, !isSingleShortSuccess);
  const playerRef = useRef<HTMLVideoElement>(null);
  const onInteractionClick = (action: string) => {
    const interaction = action as InteractionType;
    if (interaction === 'comment-open') {
      onCommentsPanelOpen?.();
      return;
    }
    if (interaction === 'comment-close') {
      onCommentsPanelClose?.();
      return;
    }
    if (interaction === 'back') {
      closeHandler();
      return;
    }
    if (interaction.includes('product')) {
      const clickedProductId = interaction.split('-').at(-1);
      const slug = productsData.find(product => product.id === clickedProductId)?.slug;
      slug && setSelectedProductSlug(slug);
    }
  };
  const sendAction = isLoggedIn ? undefined : () => navigateLogin(true);
  const closeBabyPDP = () => setSelectedProductSlug(undefined);
  useEffect(() => {
    if (!playerRef.current) return;
    if (isActive) {
      playerRef.current?.play?.();
    } else {
      playerRef.current?.pause?.();
    }
  }, [isActive]);
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!playerRef.current || !isActive) return;
      const player = playerRef.current as unknown as InteractifyPlayerRef;
      const isPlayerMuted = player?.isMuted?.();
      if (isPlayerMuted === isVideoMuted) return;
      if (isVideoMuted) {
        player?.mute?.();
      } else {
        player?.unmute?.();
      }
    }, 0);
    return () => clearTimeout(timeout);
  }, [isVideoMuted, isActive]);
  const shortCover = singleShortData?.cover || short.cover || '';
  const shortVideo = singleShortData?.streamUrl || short.streamUrl || singleShortData?.video || '';
  const shortTitle = singleShortData?.title || short.title || '';
  const shortCaption = singleShortData?.caption || short.caption || '';
  const connectionOptions = useMemo<InteractifyKhanoumiPlayerProps['connectionOptions']>(() => {
    if (isShortLoading || !isActive) return undefined;
    return {
      space_slug: SPACE_SLUG || '',
      slug: shortInitData?.slug || '',
      scope: 'short',
      session: shortInitData?.session || '',
      token: shortInitData?.token || '',
      user_id: shortInitData?.userId || '',
      display_name: shortInitData?.displayName?.trim() || 'کاربر خانومی'
    };
  }, [isActive, isShortLoading, shortInitData?.slug, shortInitData?.displayName, shortInitData?.session, shortInitData?.token, shortInitData?.userId]);

  // remove interactify param when short is not loaded
  useEffect(() => {
    if (!!short.streamUrl || isSingleShortLoading || isSingleShortSuccess) return;
    removeInteractifyParam();
  }, [isSingleShortLoading, isSingleShortSuccess, removeInteractifyParam, short.streamUrl]);
  return <div className='relative h-full overflow-hidden' data-sentry-component="InteractifyPlayer" data-sentry-source-file="player.tsx">
            <BabyPDP productSlug={selectedProductSlug} closeHandler={closeBabyPDP} data-sentry-element="BabyPDP" data-sentry-source-file="player.tsx" />

            <InteractifyKhanoumiPlayer ref={playerRef} key={short.slug || ''} connectionOptions={connectionOptions} mediaItem={{
      thumbnail: shortCover,
      url: shortVideo,
      id: short.slug || '',
      type: 'video'
    }} ProductCard={(product: Product) => <ProductCard product={product} />} products={isOpenedFromGallery ? [] : productsData} title={shortTitle} onInteractionClick={onInteractionClick} shareUrl={`${PUBLIC_DOMAIN_URL}/videos?vid=${short.slug || ''}`} likeCount={shortInitData?.likeCount || 0} caption={shortCaption} slug={short.slug || ''} isLiked={shortInitData?.isLiked} messageCount={shortInitData?.commentsCount || 0} sendAction={sendAction} onMuteStateChange={(value: boolean) => {
      if (!isActive) return;
      videoMuteStateChangeHandler(value);
    }} data-sentry-element="InteractifyKhanoumiPlayer" data-sentry-source-file="player.tsx" />
        </div>;
};
export default InteractifyPlayer;